import { getCountry } from "../country";
// PWA / WebAPK kamera erişim yardımcıları.
//
// Android'de "Add to home screen" ile yüklenen WebAPK içinde
// getUserMedia çağrısı, bazı cihazlarda permission UI'ı sessizce
// reddedebiliyor (kullanıcı daha önce "Don't ask again" demişse veya
// site izinleri WebAPK için sıfırlanmışsa). Sebep ne olursa olsun
// kullanıcıya net bir çıkış yolu sunmamız gerekiyor — bu modül
// hem ortam tespiti hem de "Chrome'da aç" deep-link'ini sağlıyor.

/**
 * Sayfa şu an Capacitor/Cordova native shell içinde mi koşuyor?
 *
 * Beş bağımsız sinyal kullanır — herhangi biri pozitifse native kabul
 * edilir. Eski Capacitor sürümlerinin `window.Capacitor` enjekte
 * etmediği (ya da bizim WebView'ın bunu sayfa yüklenmeden önce hazır
 * edemediği) edge case'leri kapsamak için Android WebView UA marker'ı
 * (`; wv)`) ile destek bridge sinyali de kontrol edilir.
 *
 *   • {@code window.Capacitor.isNativePlatform()} — official API.
 *   • {@code window.Capacitor.getPlatform() === 'android'|'ios'} — ek doğrulama.
 *   • {@code window.cordova} — legacy Cordova shell.
 *   • {@code window.webkit.messageHandlers.bridge} — iOS WKWebView bridge.
 *   • {@code navigator.userAgent} içinde `; wv)` — Android System WebView.
 */
export function isCapacitorNative() {
  if (typeof window === "undefined") return false;
  try {
    const cap = window.Capacitor;
    // Mere existence of `window.Capacitor` means the native shell injected
    // its bridge — on the web we never import @capacitor/core statically,
    // so this object is ONLY present inside the APK/AAB runtime. Treat
    // this as a positive signal even if `isNativePlatform()` returns
    // false (older Capacitor versions / pre-bridge boot).
    if (cap) return true;
    if (window.cordova) return true;
    // iOS WKWebView bridge expose
    if (window.webkit && window.webkit.messageHandlers) return true;
    // Defensive UA fallback for Android System WebView (covers builds
    // where Capacitor bridge JS hasn't injected yet at startCamera time).
    // `; wv)` is the canonical signature — Chrome browser tabs lack it.
    const ua = (window.navigator && window.navigator.userAgent) || "";
    if (/Android/.test(ua) && /;\s*wv\)/.test(ua)) return true;
  } catch { /* ignore */ }
  return false;
}

/**
 * Sayfanın bir PWA (standalone WebAPK ya da iOS "Add to Home Screen")
 * içinde mi yoksa normal tarayıcı sekmesinde mi açıldığını söyler.
 *
 * KRİTİK: Capacitor/Cordova native shell de `display-mode: standalone`'da
 * koşar ve Android WebView içindedir — ama PWA DEĞİLDİR. Native ise
 * HER ZAMAN FALSE döner, yoksa kullanıcıya yanlışlıkla Chrome fallback
 * akışı gösterilir. PWA tespiti yalnızca native değilse yapılır.
 */
export function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  if (isCapacitorNative()) return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.matchMedia && window.matchMedia("(display-mode: fullscreen)").matches) return true;
    // iOS Safari
    if (window.navigator && window.navigator.standalone === true) return true;
    // Android WebAPK kendini referrer ile belli eder
    if (typeof document !== "undefined" && document.referrer && document.referrer.startsWith("android-app://")) return true;
  } catch { /* ignore */ }
  return false;
}

/**
 * `getUserMedia` reddine veya cihaz yokluğuna işaret eden DOMException
 * adları. Aşağıdakilerin hepsini "izin yok / cihaz yok" olarak ele alıyoruz.
 */
const DENIAL_ERRORS = new Set([
  "NotAllowedError",       // user denied OR top-level frame doesn't have permission
  "PermissionDeniedError", // legacy alias
  "SecurityError",         // insecure context / iframe permissions-policy
  "NotFoundError",         // no camera device
  "OverconstrainedError",  // requested constraints not satisfiable
  "AbortError",            // app suspended mid-request
]);

export function isCameraDenialError(err) {
  if (!err) return false;
  const name = String(err.name || "");
  if (DENIAL_ERRORS.has(name)) return true;
  // Some Android WebViews surface the error as plain text only.
  const msg = String(err.message || "").toLowerCase();
  return msg.includes("permission") || msg.includes("denied") || msg.includes("not allowed");
}

/**
 * Mevcut sayfayı tarayıcıda açar — Capacitor WebView'da `intent://`
 * şemaları `ERR_UNKNOWN_URL_SCHEME` ile patladığı için artık doğrudan
 * `https://sokakkedileri.tr/scan-cat` URL'iyle `window.open('_blank')`
 * kullanıyoruz. Android WebView _blank'i `Intent.ACTION_VIEW` olarak
 * sisteme delege eder → kullanıcı varsayılan tarayıcısında (Chrome
 * dahil) açar. iOS Safari ve desktop'ta yeni sekmede açılır.
 *
 * Bu fonksiyon SADECE manuel "Chrome'da aç" butonundan tetiklenir;
 * hiçbir otomatik akıştan çağrılmaz.
 *
 * @param {string} pathname Açılacak yol (ör. `/scan-cat`).
 */
export function openInChrome(pathname = "/scan-cat") {
  if (typeof window === "undefined") return;
  // MARKA-1: host ulkeye gore. Sabit birakilinca StrayPaw kullanicisi
  // "Chrome'da ac" dedinde Turk siteye gidiyordu.
  let host = "sokakkedileri.tr";
  try {
    if (getCountry() !== "tr") host = "straypaw.ai";
  } catch { /* varsayilan tr */ }
  const cleanPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const url = `https://${host}${cleanPath}`;
  try {
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    if (opened) return;
  } catch { /* fall through */ }
  // Pop-up engellendiyse veya WebView blokladıysa son çare olarak
  // mevcut sekmeyi navigate et — kullanıcı yine de tarayıcıya çıkar.
  try { window.location.href = url; } catch { /* noop */ }
}


/**
 * Android native shell'de OS-level "App info" ekranını açar (kullanıcı
 * kamera iznini buradan manuel olarak grant edebilir). Capacitor
 * registerPlugin ile MainActivity'deki NativeBridgePlugin'e bağlanır.
 * Native shell dışında (PWA/normal tarayıcı) no-op döner.
 *
 * @returns {Promise<boolean>} Native plugin çağrısı başarılıysa true.
 */
export async function openNativeAppSettings() {
  if (typeof window === "undefined") return false;
  try {
    const Capacitor = window.Capacitor;
    if (!Capacitor || typeof Capacitor.isNativePlatform !== "function" || !Capacitor.isNativePlatform()) {
      return false;
    }
    // `@capacitor/core` is part of every Capacitor app — registerPlugin
    // is a no-op cost call when the plugin name doesn't exist on the
    // native side (web fallback), so we can guard upstream with the
    // isNativePlatform check above and skip a try/catch tower here.
    const { registerPlugin } = await import("@capacitor/core");
    const NativeBridge = registerPlugin("NativeBridge");
    await NativeBridge.openAppSettings();
    return true;
  } catch {
    return false;
  }
}

/**
 * Pulls native-side camera bridge counters from `NativeBridgePlugin`.
 * Returns null on the web (no Capacitor) or when the plugin isn't
 * registered yet. The shape mirrors what {@link MainActivity} surfaces:
 *
 *   {
 *     androidCameraPermissionGranted: boolean,
 *     onPermissionRequestCalled: number,
 *     webViewGrantCalled: number,
 *     webViewDenyCalled: number,
 *     onRequestPermissionsResultCalled: number,
 *     lastRequestedResources: string,   // e.g. "[video/capture]"
 *     packageName: string,
 *   }
 */
export async function getNativeCameraDebug() {
  if (typeof window === "undefined") return null;
  try {
    if (!window.Capacitor) return null;
    const { registerPlugin } = await import("@capacitor/core");
    const NativeBridge = registerPlugin("NativeBridge");
    return await NativeBridge.getCameraPermissionState();
  } catch {
    return null;
  }
}


/**
 * Native Android (Capacitor) için 3-açılı kedi fotoğraf çekim akışı.
 * `@capacitor/camera` plugin'iyle native kamera app'ini açıp her açı
 * için ayrı bir fotoğraf alır. WebView `getUserMedia` permission
 * köprüsünü tamamen baypas eder — kamera izni plugin tarafından OS
 * seviyesinde istenir.
 *
 * @param {(angleIndex:number, total:number) => void} onProgress
 *        Her başarılı yakalama sonrası UI feedback'i için çağrılır.
 * @returns {Promise<string[]>}
 *        3 elemanlı `data:image/jpeg;base64,...` array'i (data URL'leri).
 * @throws {Error & {name: 'NotAllowedError'|'UserCancel'|'Generic'}}
 *        İzin reddi, kullanıcı iptali veya başka hata durumlarında.
 */
export async function captureThreePhotosNative(onProgress) {
  // 1) Try the pure-native AndroidCamera JS bridge first (MainActivity
  //    exposes `window.AndroidCamera.capturePhoto(slot)`). This launches
  //    a real ACTION_IMAGE_CAPTURE intent — the only path that reliably
  //    opens the camera app on every Android WebView OEM build (some
  //    open the gallery picker for <input capture>).
  if (typeof window !== "undefined" && window.AndroidCamera && typeof window.AndroidCamera.capturePhoto === "function") {
    try {
      return await captureThreePhotosAndroidBridge(onProgress);
    } catch (e) {
      if (e?.name === "UserCancel" || e?.name === "NotAllowedError") throw e;
      // Other native errors → fall through to next strategy.
    }
  }
  // 2) Try @capacitor/camera if Capacitor bridge is present.
  if (typeof window !== "undefined" && window.Capacitor) {
    try {
      const mod = await import("@capacitor/camera");
      const { Camera, CameraResultType, CameraSource } = mod;
      const photos = [];
      for (let i = 0; i < 3; i++) {
        try {
          const photo = await Camera.getPhoto({
            quality: 85,
            allowEditing: false,
            resultType: CameraResultType.DataUrl,
            source: CameraSource.Camera,
            saveToGallery: false,
            direction: "REAR",
            presentationStyle: "fullscreen",
          });
          const dataUrl = photo?.dataUrl;
          if (!dataUrl) throw new Error("Foto verisi boş döndü");
          photos.push(dataUrl);
          if (typeof onProgress === "function") onProgress(i + 1, 3);
        } catch (e) {
          const raw = String(e?.message || e?.errorMessage || "");
          const lower = raw.toLowerCase();
          if (lower.includes("cancel") || lower.includes("iptal")) {
            const err = new Error("Kullanıcı iptal etti");
            err.name = "UserCancel";
            err.capturedSoFar = photos;
            throw err;
          }
          if (lower.includes("permission") || lower.includes("denied") || lower.includes("not granted")) {
            const err = new Error("Kamera izni reddedildi");
            err.name = "NotAllowedError";
            throw err;
          }
          if (i === 0) break;
          const err = new Error(raw || "Native kamera hatası");
          err.name = "Generic";
          err.capturedSoFar = photos;
          throw err;
        }
      }
      if (photos.length === 3) return photos;
    } catch (e) {
      if (e && (e.name === "UserCancel" || e.name === "NotAllowedError")) throw e;
    }
  }
  // 3) Last-resort fallback: <input type="file" capture="environment">.
  //    On older WebViews this opens the gallery picker, but on Chrome
  //    browser it correctly delegates to the camera app.
  return captureThreePhotosFileInput(onProgress);
}


/**
 * Native Android'de `<input type="file" capture="environment">` ile
 * 3 fotoğraf yakalama akışı. Bu yöntem Capacitor JS bridge'ine
 * BAĞLI DEĞİLDİR — Android WebView'ın yerleşik file-picker akışını
 * kullanır. Kamera izni gerekmez çünkü çekim native Camera app'i
 * üzerinden Intent ile gerçekleşir; WebView yalnızca dönen File'ı alır.

/**
 * Reports the availability of every native camera bridge path the app
 * supports — used by the in-app debug overlay so QA / users on Samsung
 * SM-A176B (and other OEM-skinned Android builds) can pinpoint which
 * specific injection failed.
 *
 *   • capacitorRuntimeAvailable    — `window.Capacitor` exists
 *   • capacitorPluginAvailable     — our `AndroidCamera` Capacitor plugin
 *     is registered (preferred path, immune to addJavascriptInterface
 *     timing issues since plugins are bound BEFORE the page loads).
 *   • androidBridgeAvailable       — legacy `window.AndroidCamera`
 *     (addJavascriptInterface). On Capacitor 7 this is added AFTER the
 *     page has started loading, so it may legitimately be undefined.
 *   • androidCaptureFunctionAvailable — the legacy bridge exists AND
 *     exposes a callable `capturePhoto` method.
 */
export function getNativeBridgeAvailability() {
  if (typeof window === "undefined") {
    return {
      capacitorRuntimeAvailable: false,
      capacitorPluginAvailable: false,
      androidBridgeAvailable: false,
      androidCaptureFunctionAvailable: false,
    };
  }
  const capacitorRuntimeAvailable = !!window.Capacitor;
  // Capacitor exposes registered plugins under `Capacitor.Plugins.<name>`.
  // We register the plugin under name "AndroidCamera" in MainActivity.
  let capacitorPluginAvailable = false;
  try {
    capacitorPluginAvailable = !!(window.Capacitor
      && window.Capacitor.Plugins
      && window.Capacitor.Plugins.AndroidCamera
      && typeof window.Capacitor.Plugins.AndroidCamera.capturePhoto === "function");
  } catch { /* ignore */ }
  const androidBridgeAvailable = !!window.AndroidCamera;
  const androidCaptureFunctionAvailable = !!(window.AndroidCamera
    && typeof window.AndroidCamera.capturePhoto === "function");
  return {
    capacitorRuntimeAvailable,
    capacitorPluginAvailable,
    androidBridgeAvailable,
    androidCaptureFunctionAvailable,
  };
}

/**
 * Capacitor-Plugin tabanlı ACTION_IMAGE_CAPTURE çağrısı —
 * {@link AndroidCameraPlugin} (Java) ile entegre. Capacitor plugin'leri
 * sayfa yüklenmeden önce register edildiği için legacy
 * `addJavascriptInterface` zamanlama sorunundan etkilenmez. Real device
 * verification (SM-A176B, Feb 2026) bu yolun gerçekten çalıştığını
 * doğruladı.
 *
 * @returns {Promise<string>} data:image/jpeg;base64,... data URL.
 * @throws {Error & {name: 'UserCancel'|'BridgeMissing'|'Generic'}}
 */
export async function captureViaCapacitorPlugin(slot) {
  if (typeof window === "undefined"
      || !window.Capacitor
      || !window.Capacitor.Plugins
      || !window.Capacitor.Plugins.AndroidCamera) {
    const err = new Error("Capacitor AndroidCamera plugin yok");
    err.name = "BridgeMissing";
    throw err;
  }
  const AndroidCameraPlugin = window.Capacitor.Plugins.AndroidCamera;
  let res;
  try {
    res = await AndroidCameraPlugin.capturePhoto({ slot });
  } catch (e) {
    const err = new Error(String(e?.message || e || "Plugin call failed"));
    err.name = "Generic";
    throw err;
  }
  if (!res || res.error) {
    const code = res?.error || "UNKNOWN";
    const err = new Error(code);
    err.name = code === "USER_CANCELLED" ? "UserCancel" : "Generic";
    throw err;
  }
  return res.dataUrl;
}

/**
 * MainActivity'deki `AndroidCamera` JavascriptInterface üzerinden
 * ACTION_IMAGE_CAPTURE intent'ini tetikler. Capacitor JS bridge eksik
 * olsa bile native intent açar — `<input capture>`'in galeri seçici
 * açtığı WebView'larda tek güvenilir yol.
 *
 * @returns {Promise<string>} data:image/jpeg;base64,... data URL.
 */
export function captureViaAndroidBridge(slot) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.AndroidCamera || typeof window.AndroidCamera.capturePhoto !== "function") {
      const err = new Error("AndroidCamera bridge yok");
      err.name = "BridgeMissing";
      reject(err);
      return;
    }
    // Tek seferlik callback — slot match kontrolüyle race koşulu yok.
    const prev = window.__onAndroidPhoto;
    window.__onAndroidPhoto = (payload) => {
      // Eski handler varsa zincirle (defensive)
      if (typeof prev === "function") { try { prev(payload); } catch { /* noop */ } }
      window.__onAndroidPhoto = prev || null;
      if (!payload || payload.error) {
        const err = new Error(payload?.error || "Native kamera hatası");
        err.name = payload?.error === "USER_CANCELLED" ? "UserCancel" : "Generic";
        reject(err);
        return;
      }
      resolve(payload.dataUrl);
    };
    try {
      window.AndroidCamera.capturePhoto(slot);
    } catch (e) {
      window.__onAndroidPhoto = prev || null;
      reject(e);
    }
  });
}

export async function captureThreePhotosAndroidBridge(onProgress) {
  const photos = [];
  for (let i = 0; i < 3; i++) {
    try {
      const dataUrl = await captureViaAndroidBridge(i);
      photos.push(dataUrl);
      if (typeof onProgress === "function") onProgress(i + 1, 3);
    } catch (e) {
      if (e?.name === "UserCancel") {
        e.capturedSoFar = photos;
      }
      throw e;
    }
  }
  return photos;
}

/**
 * Pure gallery-picker flow — opens a multi-select file input WITHOUT the
 * `capture` attribute so Android WebView reliably opens the photo gallery
 * (NOT the camera). Used as a fallback when ACTION_IMAGE_CAPTURE fails.
 *
 * Requires the user to pick exactly {@code expected} photos. Returns
 * data URLs in selection order.
 *
 * @param {number} expected How many photos to require (default 3).
 * @returns {Promise<string[]>} Array of `data:image/...;base64,...` URLs.
 * @throws {Error & {name: 'UserCancel'|'WrongCount'|'Generic'}}
 */
export async function pickPhotosFromGallery(expected = 3) {
  if (typeof document === "undefined") {
    const err = new Error("Document not available");
    err.name = "Generic";
    throw err;
  }
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    // Intentionally NO `capture` attribute — that's what forces the
    // WebView to skip the camera and go straight to the gallery.
    input.style.position = "fixed";
    input.style.left = "-9999px";
    input.style.opacity = "0";
    let settled = false;
    const cleanup = () => {
      try { document.body.removeChild(input); } catch { /* noop */ }
    };
    input.addEventListener("change", async () => {
      if (settled) return;
      settled = true;
      const files = Array.from(input.files || []);
      cleanup();
      if (files.length === 0) {
        const err = new Error("Kullanıcı iptal etti");
        err.name = "UserCancel";
        reject(err);
        return;
      }
      if (files.length !== expected) {
        const err = new Error(`Tam ${expected} fotoğraf seçmelisin`);
        err.name = "WrongCount";
        err.picked = files.length;
        reject(err);
        return;
      }
      try {
        const urls = await Promise.all(files.map((f) => new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result || ""));
          r.onerror = () => rej(new Error("Foto okunamadı"));
          r.readAsDataURL(f);
        })));
        resolve(urls);
      } catch (e) {
        const err = new Error(e?.message || "Foto okunamadı");
        err.name = "Generic";
        reject(err);
      }
    });
    // Cancel detection — Android WebView fires window focus when the
    // picker closes without selection.
    const onWindowFocus = () => {
      setTimeout(() => {
        if (settled) return;
        if (input.files && input.files.length > 0) return;
        settled = true;
        cleanup();
        const err = new Error("Kullanıcı iptal etti");
        err.name = "UserCancel";
        reject(err);
      }, 800);
    };
    window.addEventListener("focus", onWindowFocus, { once: true });
    document.body.appendChild(input);
    input.click();
  });
}


/**
 * Legacy `<input type="file" capture="environment">` 3-photo flow.
 * Kept as a last-resort fallback when even the gallery picker is
 * unavailable (e.g. PWA on iOS Safari).
 */
export async function captureThreePhotosFileInput(onProgress) {
  if (typeof document === "undefined") {
    const err = new Error("Document not available");
    err.name = "Generic";
    throw err;
  }
  const photos = [];
  for (let i = 0; i < 3; i++) {
    const dataUrl = await new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.capture = "environment"; // rear camera hint
      input.style.position = "fixed";
      input.style.left = "-9999px";
      input.style.opacity = "0";
      let settled = false;
      const cleanup = () => {
        try { document.body.removeChild(input); } catch { /* noop */ }
      };
      input.addEventListener("change", () => {
        if (settled) return;
        settled = true;
        const file = input.files && input.files[0];
        cleanup();
        if (!file) {
          const err = new Error("Kullanıcı iptal etti");
          err.name = "UserCancel";
          reject(err);
          return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => {
          const err = new Error("Foto okunamadı");
          err.name = "Generic";
          reject(err);
        };
        reader.readAsDataURL(file);
      });
      // Bazı WebView'larda kullanıcı geri tuşuyla çıkarsa `change`
      // olayı hiç gelmeyebilir. focus event'i ile yakalayalım:
      const onWindowFocus = () => {
        setTimeout(() => {
          if (settled) return;
          if (input.files && input.files.length > 0) return;
          settled = true;
          cleanup();
          const err = new Error("Kullanıcı iptal etti");
          err.name = "UserCancel";
          reject(err);
        }, 700);
      };
      window.addEventListener("focus", onWindowFocus, { once: true });
      document.body.appendChild(input);
      input.click();
    });
    photos.push(dataUrl);
    if (typeof onProgress === "function") onProgress(i + 1, 3);
  }
  return photos;
}
